<template>
  <div class="surveys-admin-page">
    <div class="card">
      <div class="card-header d-flex align-items-center justify-content-between">
        <h4 class="card-title mb-0">
          <i class="bi bi-clipboard-data me-2"></i>Surveys I'm Invited To
        </h4>
      </div>

      <div class="card-body">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- No surveys message -->
        <div v-else-if="surveys.length === 0" class="text-center py-4">
          <div class="text-muted">
            <i class="bi bi-inbox display-4 d-block mb-3"></i>
            <h5>No survey invitations yet</h5>
            <p>You haven't been invited to any surveys at this time.</p>
          </div>
        </div>

        <!-- Table Controls -->
        <div v-else class="d-flex justify-content-between align-items-center mb-3">
          <div class="d-flex align-items-center">
            <label class="me-2">Show</label>
            <select v-model="entriesPerPage" class="form-select form-select-sm me-2" style="width: auto;">
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span>entries</span>
          </div>
          <div class="d-flex align-items-center">
            <label class="me-2">Search:</label>
            <input
              v-model="searchQuery"
              type="text"
              class="form-control form-control-sm"
              placeholder="Search surveys..."
              style="width: 200px;"
            />
          </div>
        </div>

        <!-- Surveys Table -->
        <div v-if="!loading && surveys.length > 0" class="table-responsive">
          <table class="table table-hover">
            <thead>
            <tr>
              <th>SURVEY TITLE</th>
              <th>DESCRIPTION</th>
              <th>STATUS</th>
              <th>SUBMITTED DATE</th>
              <th>ACTIONS</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(survey, index) in paginatedSurveys" :key="survey.id">
              <td>
                <strong>{{ survey.title }}</strong>
              </td>
              <td>
                <span class="text-muted" style="white-space: pre-line;">{{
                    survey.description || 'No description provided'
                  }}</span>
              </td>
              <td>
                <span class="badge" :class="getStatusClass(survey.status)">
                  {{ survey.status }}
                </span>
              </td>
              <td>
                <span v-if="survey.submitted_at" class="text-muted">
                  {{ formatDate(survey.submitted_at) }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <div :class="['btn-group', { dropup: shouldOpenDropup(index) }]">
                  <button
                    class="btn btn-sm btn-outline-secondary dropdown-toggle"
                    type="button"
                    @click="toggleDropdown(survey.id)"
                    :class="{ 'show': openDropdown === survey.id }"
                  >
                    <i class="bi bi-three-dots"></i>
                  </button>
                  <ul
                    class="dropdown-menu"
                    :class="{ 'show': openDropdown === survey.id }"
                    @click="closeDropdown"
                  >
                    <li v-if="survey.status === 'Unread'">
                      <button class="dropdown-item" @click="acceptAndOpen(survey)">
                        <i class="bi bi-envelope-open me-2"></i>Accept and Open
                      </button>
                    </li>
                    <li v-if="survey.status === 'In Progress'">
                      <button class="dropdown-item" @click="continueSurvey(survey)">
                        <i class="bi bi-play-circle me-2"></i>Continue Survey
                      </button>
                    </li>
                    <li v-if="survey.status === 'In Progress'">
                      <button class="dropdown-item" @click="submitSurvey(survey)">
                        <i class="bi bi-check-circle me-2"></i>Submit Survey
                      </button>
                    </li>
                    <li v-if="survey.status === 'Completed'">
                      <button class="dropdown-item" @click="viewSubmission(survey)">
                        <i class="bi bi-eye me-2"></i>View Submission
                      </button>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="!loading && filteredSurveys.length > 0"
             class="d-flex justify-content-between align-items-center mt-3">
          <div class="text-muted">
            Showing {{ startEntry }} to {{ endEntry }} of {{ filteredSurveys.length }} entries
          </div>
          <nav aria-label="Table navigation">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="currentPage = Math.max(1, currentPage - 1)">
                  Previous
                </button>
              </li>
              <li
                v-for="page in totalPages"
                :key="page"
                class="page-item"
                :class="{ active: currentPage === page }"
              >
                <button class="page-link" @click="currentPage = page">
                  {{ page }}
                </button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link"
                        @click="currentPage = Math.min(totalPages, currentPage + 1)">
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Survey Questions Dialog -->
    <div
      v-if="showSurveyDialog && selectedSurvey"
      class="modal fade show"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      @click.self="closeSurveyDialog"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-clipboard-data me-2"></i>
              {{ selectedSurvey.status === 'Completed' ? 'Viewing Survey' : 'Answering Survey' }} -
              {{ surveyData?.title }}
            </h5>
            <button
              type="button"
              class="survey-dialog-close"
              data-test="survey-close-icon"
              @click="closeSurveyDialog"
              aria-label="Close"
            >
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="surveyLoading" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading survey...</span>
              </div>
            </div>
            <div v-else-if="surveyData">
              <div class="survey-subtitle text-muted mb-4" style="white-space: pre-line;">
                {{ surveyData.description }}
              </div>
              <form @submit.prevent="saveSurveyAnswers">
                <!-- 🔥 ENHANCED: Show error count and list affected fields -->
                <div v-if="hasFieldErrors()" class="alert alert-danger py-3 px-4 mb-4" role="alert">
                  <div class="d-flex align-items-start">
                    <i class="bi bi-exclamation-circle-fill me-3" style="font-size: var(--font-size-lg); margin-top: 2px; flex-shrink: 0;"></i>
                    <div style="flex-grow: 1;">
                      <strong>{{ getErrorCount() }} validation error{{ getErrorCount() > 1 ? 's' : '' }} found</strong>
                      <p class="mb-0 mt-2 text-dark small">
                        Please fix the following fields before saving or submitting:
                      </p>
                      <ul class="mb-0 mt-2 ps-3">
                        <li v-for="(msg, fieldId) in fieldErrors" :key="fieldId" class="small mb-1">
                          <strong>{{ getQuestionText(fieldId) }}</strong>: {{ msg }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div
                  v-for="question in visibleQuestions"
                  :key="question.id"
                  :id="`survey-question-${question.id}`"
                  class="mb-4"
                >
                  <label class="form-label fw-semibold">
                    <span class="question-label-text">{{ question.text }}</span>
                    <span v-if="question.required" class="text-danger">*</span>
                    <span v-if="question.required" class="text-muted"> (Question required)</span>
                  </label>
                  <small v-if="question.displayCondition?.questionId" class="text-muted d-block mb-2">
                    Shown when question {{ question.displayCondition.questionId }} matches "{{ question.displayCondition.answer }}"
                  </small>

                  <!-- Textarea questions -->
                  <div v-if="question.type === 'textarea' || question.type === 'multiple_line_text'">
                    <textarea
                      :value="surveyAnswers[question.id.toString()] || ''"
                      @input="updateAnswer(question.id, $event.target.value)"
                      class="form-control textarea-multiple-line"
                      :class="{ 'has-error': fieldErrors[question.id.toString()] }"
                      rows="6"
                      wrap="soft"
                      :placeholder="question.placeholder || 'Enter your answer (multiple lines accepted)...'"
                      :readonly="selectedSurvey.status === 'Completed'"
                    ></textarea>
                  </div>

                  <!-- Text input questions -->
                  <div v-else-if="question.type === 'text' || question.type === 'single_line_text'">
                    <input
                      :value="surveyAnswers[question.id.toString()] || ''"
                      @input="updateAnswer(question.id, $event.target.value)"
                      type="text"
                      class="form-control"
                      :class="{ 'has-error': fieldErrors[question.id.toString()] }"
                      :placeholder="question.placeholder || 'Enter your answer...'"
                      :maxlength="getInputMaxLength(question)"
                      :readonly="selectedSurvey.status === 'Completed'"
                    />
                    <small v-if="question.charLimit" class="text-muted d-block mt-1">
                      Maximum {{ question.charLimit }} characters
                    </small>
                  </div>

                  <!-- Radio questions -->
                  <div v-else-if="question.type === 'radio' || question.type === 'multiple_choice_single'">
                    <div
                      v-for="option in question.options"
                      :key="option"
                      class="form-check"
                    >
                      <input
                        :id="`q${question.id}_${option}`"
                        :checked="surveyAnswers[question.id.toString()] === option"
                        @change="updateAnswer(question.id, option)"
                        class="form-check-input"
                        type="radio"
                        :name="`question_${question.id}`"
                        :disabled="selectedSurvey.status === 'Completed'"
                      />
                      <label class="form-check-label" :for="`q${question.id}_${option}`">
                        {{ option }}
                      </label>
                    </div>
                    <div v-if="question.allowOthers" class="form-check mt-2">
                      <input
                        :id="`q${question.id}___other__`"
                        :checked="surveyAnswers[question.id.toString()] === '__other__'"
                        @change="updateAnswer(question.id, '__other__')"
                        class="form-check-input"
                        type="radio"
                        :name="`question_${question.id}`"
                        :disabled="selectedSurvey.status === 'Completed'"
                      />
                      <label class="form-check-label" :for="`q${question.id}___other__`">
                        Other
                      </label>
                    </div>
                    <input
                      v-if="question.allowOthers && surveyAnswers[question.id.toString()] === '__other__'"
                      :value="otherAnswers[question.id.toString()] || ''"
                      @input="updateOtherAnswer(question.id, $event.target.value)"
                      type="text"
                      class="form-control form-control-sm mt-2"
                      placeholder="Please specify"
                      :readonly="selectedSurvey.status === 'Completed'"
                    />
                  </div>

                  <!-- Checkbox questions -->
                  <div v-else-if="question.type === 'checkbox' || question.type === 'multiple_choice_multiple'">
                    <div
                      v-for="option in question.options"
                      :key="option"
                      class="form-check"
                    >
                      <input
                        :id="`q${question.id}_${option}`"
                        :checked="Array.isArray(surveyAnswers[question.id.toString()]) && surveyAnswers[question.id.toString()].includes(option)"
                        @change="updateCheckboxAnswer(question.id, option, $event.target.checked)"
                        class="form-check-input"
                        type="checkbox"
                        :disabled="selectedSurvey.status === 'Completed'"
                      />
                      <label class="form-check-label" :for="`q${question.id}_${option}`">
                        {{ option }}
                      </label>
                    </div>
                    <div v-if="question.allowOthers" class="form-check mt-2">
                      <input
                        :id="`q${question.id}___other__`"
                        :checked="Array.isArray(surveyAnswers[question.id.toString()]) && surveyAnswers[question.id.toString()].includes('__other__')"
                        @change="updateCheckboxAnswer(question.id, '__other__', $event.target.checked)"
                        class="form-check-input"
                        type="checkbox"
                        :disabled="selectedSurvey.status === 'Completed'"
                      />
                      <label class="form-check-label" :for="`q${question.id}___other__`">
                        Other
                      </label>
                    </div>
                    <input
                      v-if="question.allowOthers && Array.isArray(surveyAnswers[question.id.toString()]) && surveyAnswers[question.id.toString()].includes('__other__')"
                      :value="otherAnswers[question.id.toString()] || ''"
                      @input="updateOtherAnswer(question.id, $event.target.value)"
                      type="text"
                      class="form-control form-control-sm mt-2"
                      placeholder="Please specify"
                      :readonly="selectedSurvey.status === 'Completed'"
                    />
                  </div>

                  <!-- Dropdown questions -->
                  <div v-else-if="question.type === 'select' || question.type === 'dropdown'">
                    <select
                      class="form-select"
                      :value="surveyAnswers[question.id.toString()] || ''"
                      @change="updateAnswer(question.id, $event.target.value)"
                      :disabled="selectedSurvey.status === 'Completed'"
                    >
                      <option value="">Select an option...</option>
                      <option v-for="option in question.options" :key="option" :value="option">{{ option }}</option>
                      <option v-if="question.allowOthers" value="__other__">Other</option>
                    </select>
                    <input
                      v-if="question.allowOthers && surveyAnswers[question.id.toString()] === '__other__'"
                      :value="otherAnswers[question.id.toString()] || ''"
                      @input="updateOtherAnswer(question.id, $event.target.value)"
                      type="text"
                      class="form-control form-control-sm mt-2"
                      placeholder="Please specify"
                      :readonly="selectedSurvey.status === 'Completed'"
                    />
                  </div>

                  <!-- Ranking questions -->
                  <div v-else-if="question.type === 'ranking' || question.type === 'ranking_scale'">
                    <RankingQuestion
                      :options="Array.isArray(question.options) ? question.options : []"
                      :model-value="getRankingAnswerModel(question.id)"
                      :disabled="selectedSurvey.status === 'Completed'"
                      @update:model-value="updateRankingAnswer(question.id, $event)"
                    />
                  </div>

                  <!-- Rating scale questions -->
                  <div v-else-if="question.type === 'rating_scale' || question.type === 'rating'">
                    <div class="rating-scale-container">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="text-muted small">{{ question.scaleMinLabel || getRatingScaleMin(question) }}</span>
                        <span class="text-muted small">{{ question.scaleMaxLabel || getRatingScaleMax(question) }}</span>
                      </div>
                      <div class="d-flex flex-wrap gap-2">
                        <label
                          v-for="value in getRatingScaleValues(question)"
                          :key="`q${question.id}_rating_${value}`"
                          class="form-check form-check-inline mb-0"
                        >
                          <input
                            class="form-check-input"
                            type="radio"
                            :name="`question_${question.id}`"
                            :value="value.toString()"
                            :checked="surveyAnswers[question.id.toString()]?.toString() === value.toString()"
                            @change="updateAnswer(question.id, value.toString())"
                            :disabled="selectedSurvey.status === 'Completed'"
                          />
                          <span class="form-check-label">{{ value }}</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- File upload questions -->
                  <div v-else-if="question.type === 'file_upload'">
                    <FileUploadQuestion
                      :value="surveyAnswers[question.id.toString()] || ''"
                      :question="question"
                      :survey-id="selectedSurvey.id"
                      @input="updateAnswer(question.id, $event)"
                    />
                  </div>

                  <!-- 🔥 ENHANCED: Better error styling for field-level errors -->
                  <div v-if="fieldErrors[question.id.toString()]"
                       class="alert alert-danger alert-sm mt-3 mb-0 py-2 px-3"
                       role="alert">
                    <i class="bi bi-exclamation-triangle-fill me-2"></i>
                    {{ fieldErrors[question.id.toString()] }}
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div class="modal-footer" v-if="selectedSurvey.status !== 'Completed'">
            <button type="button" class="btn btn-secondary" @click="closeSurveyDialog">
              Cancel
            </button>
            <!-- 🔥 ENHANCED: Disable save button when validation errors exist -->
            <span v-if="!surveyLoading && openRequiredQuestions.length" class="text-danger small me-auto" data-test="required-hint">
              {{ openRequiredQuestions.length }} required question{{ openRequiredQuestions.length > 1 ? 's' : '' }} still to answer before you can submit. You can still save your answers.
            </span>
            <button type="button" class="btn btn-primary" @click="saveSurveyAnswers"
                    :disabled="surveyLoading || saving || hasBlockingErrors()"
                    :title="hasBlockingErrors() ? 'Please fix validation errors first' : ''">
              <i class="bi bi-save me-2"></i>
              {{ saving ? 'Saving...' : 'Save Answers' }}
            </button>
            <!-- The wrapper takes the click of the disabled button, to highlight what is still open -->
            <span class="save-submit-wrap" data-test="save-submit-wrap" @click="highlightOpenRequired"
                  :title="canSubmit ? '' : 'Answer all required questions first (click to show them)'">
              <button type="button" class="btn btn-success" data-test="save-submit"
                      @click="saveAndSubmit"
                      :disabled="surveyLoading || saving || submitting || hasBlockingErrors() || !canSubmit">
                <i class="bi bi-check-circle me-2"></i>
                {{ saving || submitting ? 'Working...' : 'Save & Submit' }}
              </button>
            </span>
          </div>
          <div class="modal-footer" v-else>
            <button type="button" class="btn btn-secondary" @click="closeSurveyDialog">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit Survey Confirmation Dialog -->
    <div
      v-if="showSubmitDialog"
      class="modal fade show"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      @click.self="showSubmitDialog = false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-question-circle text-primary me-2"></i>
              Confirm Survey Submission
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="showSubmitDialog = false"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">
              Do you want to submit this survey? Once submitted, you will no longer be able to edit
              your answers.
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showSubmitDialog = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="confirmSubmit"
              :disabled="submitting"
            >
              {{ submitting ? 'Submitting...' : 'Submit Survey' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Toast Notification -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
      <div class="toast" :class="{ show: toast.show, 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error' }" role="alert">
        <div class="toast-header" :class="{ 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error', 'text-white': true }">
          <i class="bi me-2" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
          <strong class="me-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
        </div>
        <div class="toast-body text-white">
          {{ toast.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import SurveyService from '@/services/surveys/SurveyService'
import AnalyticsService from '@/services/analytics/AnalyticsService'
import RankingQuestion from '@/components/common/surveys/RankingQuestion.vue'
import FileUploadQuestion from '@/components/common/surveys/FileUploadQuestion.vue'

const REQUIRED_MESSAGE = 'This question is required.'

export default {
  name: 'MySurveys',
  components: {
    RankingQuestion,
    FileUploadQuestion
  },
  data() {
    return {
      toast: {
        show: false,
        message: '',
        type: 'success'
      }
    }
  },

  setup() {
    // Reactive data
    const surveys = ref([])
    const loading = ref(false)
    const searchQuery = ref('')
    const entriesPerPage = ref(10)
    const currentPage = ref(1)
    const showSubmitDialog = ref(false)
    const showSurveyDialog = ref(false)
    const selectedSurvey = ref(null)
    const surveyData = ref(null)
    const surveyLoading = ref(false)
    const saving = ref(false)
    const submitting = ref(false)
    const surveyAnswers = reactive({})
    const otherAnswers = reactive({})
    const fieldErrors = reactive({})
    const existingAnswers = ref({})
    const answerId = ref(null)
    const openDropdown = ref(null)

    return {
      surveys,
      loading,
      searchQuery,
      entriesPerPage,
      currentPage,
      showSubmitDialog,
      showSurveyDialog,
      selectedSurvey,
      surveyData,
      surveyLoading,
      saving,
      submitting,
      surveyAnswers,
      otherAnswers,
      fieldErrors,
      existingAnswers,
      answerId,
      openDropdown,
      // nextTick
    }
  },

  computed: {
    filteredSurveys() {
      if (!this.searchQuery) {
        return this.surveys
      }
      return this.surveys.filter(survey =>
        survey.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        survey.status.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (survey.description && survey.description.toLowerCase().includes(this.searchQuery.toLowerCase()))
      )
    },
    visibleQuestions() {
      return (this.surveyData?.questions || []).filter(question => this.isQuestionVisible(question))
    },

    // True when every visible required question has an answer. It follows the same
    // visibility and answer rules as validateResponses, so a required question that a
    // display condition hides (or an "Other" with no text) is judged the same way here.
    openRequiredQuestions() {
      return this.visibleQuestions.filter(question => this.isRequiredQuestionOpen(question))
    },
    canSubmit() {
      if (!this.surveyData?.questions) return false

      return this.openRequiredQuestions.length === 0
    },
    totalPages() {
      return Math.ceil(this.filteredSurveys.length / this.entriesPerPage)
    },
    paginatedSurveys() {
      const start = (this.currentPage - 1) * this.entriesPerPage
      const end = start + this.entriesPerPage
      return this.filteredSurveys.slice(start, end)
    },
    startEntry() {
      if (this.filteredSurveys.length === 0) return 0
      return (this.currentPage - 1) * this.entriesPerPage + 1
    },
    endEntry() {
      return Math.min(this.currentPage * this.entriesPerPage, this.filteredSurveys.length)
    }
  },

  async mounted() {
    await this.loadMySurveys()
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    shouldOpenDropup(rowIndex) {
      const visibleRows = this.paginatedSurveys.length
      return visibleRows <= 2 || rowIndex >= visibleRows - 2
    },
    showToast(type, message) {
      this.toast = {
        show: true,
        type: type,
        message: message
      }
      setTimeout(() => {
        this.toast.show = false
      }, 3000)
    },

    updateAnswer(questionId, value) {
      const key = questionId.toString()
      this.surveyAnswers[key] = value

      // 🔥 NEW: Clear field error when user starts typing (improves UX)
      if (this.fieldErrors[key]) {
        delete this.fieldErrors[key]
      }

      if (value !== '__other__' && this.otherAnswers[key]) {
        delete this.otherAnswers[key]
      }

      if (this.hasFieldErrors()) {
        this.validateResponses()
      }
    },

    updateOtherAnswer(questionId, value) {
      const key = questionId.toString()
      this.otherAnswers[key] = value

      // 🔥 NEW: Clear field error when user starts typing (improves UX)
      if (this.fieldErrors[key]) {
        delete this.fieldErrors[key]
      }

      if (this.hasFieldErrors()) {
        this.validateResponses()
      }
    },

    updateCheckboxAnswer(questionId, option, checked) {
      const key = questionId.toString()

      if (!Array.isArray(this.surveyAnswers[key])) {
        this.surveyAnswers[key] = []
      }

      if (checked) {
        if (!this.surveyAnswers[key].includes(option)) {
          this.surveyAnswers[key].push(option)
        }
      } else {
        const index = this.surveyAnswers[key].indexOf(option)
        if (index > -1) {
          this.surveyAnswers[key].splice(index, 1)
        }
      }

      if (this.hasFieldErrors()) {
        this.validateResponses()
      }
    },

    getRankingAnswerModel(questionId) {
      const key = questionId.toString()
      const currentValue = this.surveyAnswers[key]

      if (!currentValue || typeof currentValue !== 'object' || Array.isArray(currentValue)) {
        return {}
      }

      return currentValue
    },

    updateRankingAnswer(questionId, value) {
      const key = questionId.toString()
      this.surveyAnswers[key] = value && typeof value === 'object' ? value : {}

      if (this.hasFieldErrors()) {
        this.validateResponses()
      }
    },

    isRequiredQuestionOpen(question) {
      const isRequired = question.required === true || question.required === 'true'
      if (!isRequired) return false

      const answer = this.surveyAnswers[question.id.toString()]
      return this.getFinalAnswerValue(question, answer).trim() === ''
    },

    // Errors other than "required" stop a save; a required question may stay open when saving
    hasBlockingErrors() {
      return Object.values(this.fieldErrors).some(message => !/required/i.test(message))
    },

    // Mark the open required questions with the usual validation errors and scroll to the first
    highlightOpenRequired() {
      if (this.canSubmit || this.surveyLoading || !this.surveyData) return

      this.validateResponses()
      const first = this.openRequiredQuestions[0]
      const element = first && document.getElementById(`survey-question-${first.id}`)
      element?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
    },

    clearFieldErrors() {
      Object.keys(this.fieldErrors).forEach(key => {
        delete this.fieldErrors[key]
      })
    },

    hasFieldErrors() {
      return Object.keys(this.fieldErrors).length > 0
    },

    // 🔥 NEW: Get count of validation errors
    getErrorCount() {
      return Object.keys(this.fieldErrors).length
    },

    // 🔥 NEW: Get question text by field ID for error display
    getQuestionText(fieldId) {
      if (!this.surveyData || !this.surveyData.questions) {
        return `Question ${fieldId}`
      }
      const question = this.surveyData.questions.find(q => q.id.toString() === fieldId)
      return question ? question.text : `Question ${fieldId}`
    },

    initializeSurveyAnswers(questions, existingAnswers = {}) {
      // Clear existing answers but keep the reactive object
      Object.keys(this.surveyAnswers).forEach(key => {
        delete this.surveyAnswers[key]
      })
      Object.keys(this.otherAnswers).forEach(key => {
        delete this.otherAnswers[key]
      })
      this.clearFieldErrors()

      // Initialize each question
      questions.forEach(question => {
        const key = question.id.toString()
        if (question.type === 'checkbox' || question.type === 'multiple_choice_multiple') {
          this.surveyAnswers[key] = []
        } else if (question.type === 'ranking' || question.type === 'ranking_scale') {
          this.surveyAnswers[key] = {}
        } else {
          this.surveyAnswers[key] = ''
        }
      })

      // Load existing answers
      Object.keys(existingAnswers).forEach(questionKey => {
        const answer = existingAnswers[questionKey]
        const answerText = answer.answer_text || answer.answer_key || ''
        if (answerText) {
          const question = questions.find(q => q.id.toString() === questionKey)
          if (question && (question.type === 'checkbox' || question.type === 'multiple_choice_multiple')) {
            const parsed = answerText.includes(',')
              ? answerText.split(',').map(item => item.trim()).filter(item => item !== '')
              : [answerText]

            if (question.allowOthers) {
              const options = Array.isArray(question.options) ? question.options : []
              const selected = []
              const unknown = []

              parsed.forEach(item => {
                if (options.includes(item)) {
                  selected.push(item)
                } else {
                  unknown.push(item)
                }
              })

              if (unknown.length > 0) {
                selected.push('__other__')
                this.otherAnswers[questionKey] = unknown.join(', ')
              }

              this.surveyAnswers[questionKey] = selected
            } else {
              this.surveyAnswers[questionKey] = parsed
            }
          } else if (question && (question.type === 'ranking' || question.type === 'ranking_scale')) {
            const parsed = answerText.includes(',')
              ? answerText.split(',').map(item => item.trim()).filter(item => item !== '')
              : (answerText ? [answerText.trim()] : [])

            const optionSet = new Set(Array.isArray(question.options) ? question.options : [])
            const rankingAnswer = {}

            parsed.forEach((optionLabel, index) => {
              if (optionSet.has(optionLabel)) {
                rankingAnswer[optionLabel] = index + 1
              }
            })

            this.surveyAnswers[questionKey] = rankingAnswer
          } else {
            if (question && question.allowOthers) {
              const options = Array.isArray(question.options) ? question.options : []
              if (options.includes(answerText)) {
                this.surveyAnswers[questionKey] = answerText
              } else {
                this.surveyAnswers[questionKey] = '__other__'
                this.otherAnswers[questionKey] = answerText
              }
            } else {
              this.surveyAnswers[questionKey] = answerText
            }
          }
        }
      })
    },

    getFinalAnswerValue(question, answer) {
      const questionKey = question.id.toString()
      const otherText = (this.otherAnswers[questionKey] || '').trim()

      if (question.type === 'ranking' || question.type === 'ranking_scale') {
        if (!answer || typeof answer !== 'object' || Array.isArray(answer)) {
          return ''
        }

        return Object.entries(answer)
          .map(([option, rank]) => ({ option, rank: Number.parseInt(rank, 10) }))
          .filter(item => Number.isInteger(item.rank) && item.rank > 0)
          .sort((a, b) => a.rank - b.rank)
          .map(item => item.option)
          .join(', ')
      }

      if (Array.isArray(answer)) {
        const values = answer.filter(item => item && item !== '__other__').map(item => item.toString())
        if (answer.includes('__other__') && otherText) {
          values.push(otherText)
        }
        return values.join(', ')
      }

      if (answer === '__other__') {
        return otherText
      }

      return answer?.toString?.() || ''
    },

    getInputMaxLength(question) {
      return Number.isInteger(question?.charLimit) && question.charLimit > 0 ? question.charLimit : null
    },

    getRatingScaleMin(question) {
      const min = Number.parseInt(question?.scaleMin, 10)
      return Number.isInteger(min) ? min : 1
    },

    getRatingScaleMax(question) {
      const max = Number.parseInt(question?.scaleMax, 10)
      return Number.isInteger(max) ? max : 5
    },

    getRatingScaleValues(question) {
      const min = this.getRatingScaleMin(question)
      const max = this.getRatingScaleMax(question)
      const start = Math.min(min, max)
      const end = Math.max(min, max)
      const values = []

      for (let value = start; value <= end; value++) {
        values.push(value)
      }

      return values
    },

    getQuestionPreviewText(question) {
      return (question?.text || 'Question').replace(/\s+/g, ' ').trim()
    },

    doesAnswerMatchCondition(actualAnswer, expectedAnswer) {
      const actual = Array.isArray(actualAnswer)
        ? actualAnswer.map(item => item.toString().trim()).filter(Boolean)
        : String(actualAnswer || '').split(',').map(item => item.trim()).filter(Boolean)

      const expected = String(expectedAnswer || '')
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)

      if (!expected.length) {
        return true
      }

      return expected.some(item => actual.includes(item))
    },

    isQuestionVisible(question) {
      const condition = question?.displayCondition
      if (!condition || !condition.questionId || !String(condition.answer || '').trim()) {
        return true
      }

      if (!this.surveyData?.questions?.length) {
        return true
      }

      const parentQuestion = this.surveyData.questions.find(item => String(item.id) === String(condition.questionId))
      if (!parentQuestion) {
        return true
      }

      if (!this.isQuestionVisible(parentQuestion)) {
        return false
      }

      const parentKey = parentQuestion.id.toString()
      const parentAnswer = this.getFinalAnswerValue(parentQuestion, this.surveyAnswers[parentKey])
      return this.doesAnswerMatchCondition(parentAnswer, condition.answer)
    },

    // requireAnswers is off when only saving: a save is allowed with required questions open
    validateResponses(requireAnswers = true) {
      this.clearFieldErrors()

      if (!this.surveyData?.questions) {
        return { valid: true }
      }

      this.surveyData.questions.forEach(question => {
        if (!this.isQuestionVisible(question)) {
          return
        }

        const questionKey = question.id.toString()
        const answer = this.surveyAnswers[questionKey]
        const finalAnswer = this.getFinalAnswerValue(question, answer)
        const isRequired = question.required === true || question.required === 'true'

        if ((question.type === 'ranking' || question.type === 'ranking_scale') && answer && typeof answer === 'object' && !Array.isArray(answer)) {
          const rankValues = Object.values(answer)
            .map(rank => Number.parseInt(rank, 10))
            .filter(rank => Number.isInteger(rank) && rank > 0)

          if (rankValues.length > 0) {
            const uniqueRanks = new Set(rankValues)
            if (uniqueRanks.size !== rankValues.length) {
              this.fieldErrors[questionKey] = 'Each rank value can only be used once.'
              return
            }
          }
        }

        if (requireAnswers && isRequired && finalAnswer.trim() === '') {
          this.fieldErrors[questionKey] = REQUIRED_MESSAGE
          return
        }

        if (question.charLimit && finalAnswer.length > question.charLimit) {
          this.fieldErrors[questionKey] = `Maximum ${question.charLimit} characters allowed.`
        }
      })

      return {
        valid: !this.hasFieldErrors()
      }
    },

    handleClickOutside(event) {
      if (!event.target.closest('.btn-group')) {
        this.openDropdown = null
      }
    },

    async loadMySurveys() {
      try {
        this.loading = true
        const response = await SurveyService.getMySurveys()

        if (response && response.success && response.data) {
          this.surveys = Array.isArray(response.data) ? response.data : []
        } else if (response && response.Data && response.Data.success && response.Data.data) {
          this.surveys = Array.isArray(response.Data.data) ? response.Data.data : []
        } else if (response && Array.isArray(response)) {
          this.surveys = response
        } else {
          const errorMessage = response?.message || response?.Data?.message || 'Unknown error occurred'
          this.showToast('error', `Failed to load survey invitations: ${errorMessage}`)
          this.surveys = []
        }
      } catch (error) {
        console.error('Error loading survey invitations:', error)
        this.showToast('error', 'Error loading survey invitations')
        this.surveys = []
      } finally {
        this.loading = false
      }
    },

    toggleDropdown(surveyId) {
      this.openDropdown = this.openDropdown === surveyId ? null : surveyId
    },

    closeDropdown() {
      this.openDropdown = null
    },

    getStatusClass(status) {
      switch (status) {
        case 'In Progress':
          return 'bg-warning text-dark'
        case 'Unread':
          return 'bg-info'
        case 'Completed':
          return 'bg-success'
        default:
          return 'bg-secondary'
      }
    },

    async acceptAndOpen(survey) {
      try {
        this.showToast('info', 'Opening survey...')
        const response = await SurveyService.acceptAndOpenSurvey(survey.id)

        let success = false
        let message = null
        if (response && response.Data) {
          success = response.Data.success
          message = response.Data.message || response.Message
        } else if (response && response.success !== undefined) {
          success = response.success
          message = response.message
        }

        if (success) {
          this.showToast('success', 'Survey opened successfully!')
          AnalyticsService.trackEvent('survey_start', { survey_id: survey.id })
          await this.loadMySurveys()
          setTimeout(() => {
            const updatedSurvey = this.surveys.find(s => s.id === survey.id) || survey
            this.continueSurvey(updatedSurvey)
          }, 500)
        } else {
          const errorMsg = message || 'Failed to open survey'
          this.showToast('error', errorMsg)
        }
      } catch (error) {
        console.error('Error accepting survey:', error)
        this.showToast('error', 'Error opening survey: ' + (error.message || 'Unknown error'))
      }
    },

    async continueSurvey(survey) {
      try {
        this.selectedSurvey = survey
        this.surveyLoading = true
        this.showSurveyDialog = true
        // await this.nextTick()

        const response = await SurveyService.continueSurvey(survey.id)

        let responseData = null
        let success = false
        if (response && response.Data) {
          success = response.Data.success
          responseData = response.Data.data
        } else if (response && response.success !== undefined) {
          success = response.success
          responseData = response.data
        }

        if (success && responseData) {
          const surveyData = responseData.survey
          this.surveyData = {
            id: surveyData.id,
            title: surveyData.title,
            description: surveyData.description,
            questions: surveyData.structure && surveyData.structure.questions
              ? SurveyService.formatSurveyStructure(surveyData.structure).questions
              : []
          }

          this.existingAnswers = responseData.existing_answers || {}
          this.answerId = responseData.answer_id

          this.initializeSurveyAnswers(this.surveyData.questions, this.existingAnswers)
        } else {
          const errorMsg = response?.message || response?.Data?.message || 'Failed to load survey'
          this.showToast('error', errorMsg)
          this.closeSurveyDialog()
        }
      } catch (error) {
        console.error('Error loading survey:', error)
        this.showToast('error', 'Error loading survey: ' + (error.message || 'Unknown error'))
        this.closeSurveyDialog()
      } finally {
        this.surveyLoading = false
      }
    },

    async submitSurvey(survey) {
      // First load the survey if not already loaded
      if (!this.surveyData || this.selectedSurvey?.id !== survey.id) {
        await this.continueSurvey(survey)
        // Wait a moment for the form to load
        await new Promise(resolve => setTimeout(resolve, 10))
      }

      // Check if we have answers now
      if (!this.surveyAnswers || Object.keys(this.surveyAnswers).length === 0) {
        this.showToast('error', 'Please fill out the survey form before submitting')
        return
      }

      // A survey with required questions still open cannot be submitted
      if (!this.canSubmit) {
        this.highlightOpenRequired()
        this.showToast('error', 'Please answer all required questions before submitting. You can still save your answers.')
        return
      }

      // Show the submit dialog
      this.showSubmitDialog = true
    },

    async submitSurveyFromDialog() {
      if (!this.selectedSurvey || !this.surveyData) return

      try {
        this.submitting = true

        // Format answers correctly
        const validation = this.validateResponses()
        if (!validation.valid) {
          this.showToast('error', 'Please fix highlighted validation errors before submitting.')
          this.showSubmitDialog = false // Close dialog so the highlighted errors are visible
          return
        }

        const formattedAnswers = {}

        this.surveyData.questions.forEach(question => {
          if (!this.isQuestionVisible(question)) {
            return
          }

          const questionKey = question.id.toString()
          const answer = this.surveyAnswers[questionKey]
          const finalAnswer = this.getFinalAnswerValue(question, answer)

          if (finalAnswer.trim() !== '') {
            formattedAnswers[questionKey] = {
              answer_key: finalAnswer,
              answer_text: finalAnswer
            }
          }
        })

        // Check if we have any answers at all
        if (Object.keys(formattedAnswers).length === 0) {
          this.showToast('error', 'Please answer at least one question before submitting')
          return
        }

        // Submit the survey
        const response = await SurveyService.submitSurvey(this.selectedSurvey.id, formattedAnswers)

        let success = false
        let message = ''
        if (response && response.Data) {
          success = response.Data.success
          message = response.Data.message || response.Message || ''
        } else if (response && response.success !== undefined) {
          success = response.success
          message = response.message || ''
        }

        if (success) {
          this.showToast('success', 'Survey submitted successfully!')
          AnalyticsService.trackEvent('survey_submit', { survey_id: this.selectedSurvey.id })
          this.showSubmitDialog = false
          this.closeSurveyDialog()
          await this.loadMySurveys()
          return true
        } else {
          // 🔥 NEW: Handle field-level errors from backend
          if (response && response.field_errors && typeof response.field_errors === 'object') {
            // Backend returned field-specific errors - display them
            Object.entries(response.field_errors).forEach(([questionKey, errorMsg]) => {
              this.fieldErrors[questionKey] = errorMsg
            })
            this.showToast('error', message || 'Please review the marked fields and try again')
            this.showSubmitDialog = false // Close dialog so user can fix errors
          } else {
            // Generic error response
            this.showToast('error', message || 'Failed to submit survey')
          }
        }
      } catch (error) {
        console.error('Error submitting survey:', error)
        this.showToast('error', 'Error submitting survey: ' + (error.message || 'Unknown error'))
      } finally {
        this.submitting = false
      }
      return false
    },

    async confirmSubmit() {
      await this.submitSurveyFromDialog()
    },

    async viewSubmission(survey) {
      await this.continueSurvey(survey)
    },

    async saveSurveyAnswers() {
      if (!this.selectedSurvey || !this.surveyData) return

      try {
        this.saving = true
        const validation = this.validateResponses(false)
        if (!validation.valid) {
          this.showToast('error', 'Please fix highlighted validation errors before saving.')
          return
        }

        const formattedAnswers = {}
        this.surveyData.questions.forEach(question => {
          if (!this.isQuestionVisible(question)) {
            return
          }

          const questionKey = question.id.toString()
          const answer = this.surveyAnswers[questionKey]
          const finalAnswer = this.getFinalAnswerValue(question, answer)

          if (finalAnswer.trim() !== '') {
            formattedAnswers[questionKey] = {
              answer_key: finalAnswer,
              answer_text: finalAnswer
            }
          }
        })

        const response = await SurveyService.saveAnswers(this.selectedSurvey.id, formattedAnswers)

        console.log('Reponse: ', response)

        let success = false
        let message = null
        if (response && response.Data) {
          success = response.Data.success
          message = response.Data.message || response.Message
        } else if (response && response.success !== undefined) {
          success = response.success
          message = response.message
        }

        if (success) {
          this.showToast('success', 'Survey answers saved successfully!')
          this.clearFieldErrors() // Clear any previous errors on success
          await this.loadMySurveys()
          return true
        } else {
          // 🔥 NEW: Handle field-level errors from backend
          if (response && response.field_errors && typeof response.field_errors === 'object') {
            // Backend returned field-specific errors - display them
            Object.entries(response.field_errors).forEach(([questionKey, errorMsg]) => {
              this.fieldErrors[questionKey] = errorMsg
            })
            this.showToast('error', message || 'Please review the marked fields and try again')
          } else {
            // Generic error response
            this.showToast('error', message || 'Failed to save answers')
          }
        }
      } catch (error) {
        console.error('Error saving answers:', error)
        this.showToast('error', 'Error saving answers')
      } finally {
        this.saving = false
      }
      return false
    },

    // Save the answers, then submit; stops (popup stays open) if either step fails
    async saveAndSubmit() {
      if (!this.canSubmit || this.saving || this.submitting) return
      if (!(await this.saveSurveyAnswers())) return
      await this.submitSurveyFromDialog()
    },

    closeSurveyDialog() {
      this.showSurveyDialog = false
      this.selectedSurvey = null
      this.surveyData = null
      // Clear answers but keep the reactive object
      Object.keys(this.surveyAnswers).forEach(key => {
        delete this.surveyAnswers[key]
      })
      Object.keys(this.otherAnswers).forEach(key => {
        delete this.otherAnswers[key]
      })
      this.clearFieldErrors()
      this.existingAnswers = {}
      this.answerId = null
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },

  watch: {
    searchQuery() {
      this.currentPage = 1
    },
    entriesPerPage() {
      this.currentPage = 1
    }
  }
}
</script>

<style scoped>
.surveys-admin-page {
  padding: 1rem 0;
}

/* 🔥 NEW: Visual highlighting for form controls with errors */
.form-control.has-error,
.form-select.has-error {
  border-color: #dc3545;
  border-width: 2px;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

textarea.has-error {
  border-color: #dc3545;
  border-width: 2px;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

/* 🔥 NEW: Highlight question label when field has error */
.question-with-error .form-label {
  color: #dc3545;
  font-weight: var(--font-weight-semibold);
}

/* 🔥 NEW: Enhanced alert styling for errors */
.alert-sm {
  font-size: var(--font-size-sm);
  padding: 0.5rem 0.75rem !important;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
}

.table th {
  font-weight: var(--font-weight-semibold);
  color: #6c757d;
  font-size: var(--font-size-sm);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge {
  font-size: var(--font-size-xs);
  padding: 0.375rem 0.75rem;
}

.dropdown-toggle::after {
  display: none;
}

.dropdown-item {
  padding: 0.5rem 1rem;
  font-size: var(--font-size-sm);
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.pagination .page-link {
  color: #6c757d;
  border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
  background-color: #009CAD;
  border-color: #009CAD;
}

.pagination .page-link:hover {
  color: #009CAD;
  background-color: #e9ecef;
}

/* Modal Styles */
.modal-content {
  border-radius: 8px;
  border: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  overflow-y: auto;
}

.btn-group {
  position: relative;
}

.surveys-admin-page .table-responsive {
  overflow: visible;
}

.dropup .dropdown-menu {
  top: auto;
  bottom: 100%;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1080;
  display: none;
  min-width: 10rem;
  padding: 0.5rem 0;
  margin: 0;
  font-size: var(--font-size-sm);
  color: #212529;
  text-align: left;
  list-style: none;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
}

.dropdown-menu.show {
  display: block;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 0.375rem 1rem;
  clear: both;
  font-weight: var(--font-weight-regular);
  color: #212529;
  text-align: inherit;
  text-decoration: none;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
}

.dropdown-item:hover,
.dropdown-item:focus {
  color: #1e2125;
  background-color: #e9ecef;
}

.modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
}

.modal-title {
  color: #2c3e50;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.question-label-text {
  white-space: pre-line;
}

.save-submit-wrap .btn:disabled {
  pointer-events: none;
}

.survey-dialog-close {
  margin-left: auto;
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: #2c3e50;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
}

.survey-dialog-close:hover {
  background-color: #e9ecef;
}

.survey-dialog-close:focus-visible {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
}

.btn-close {
  background: none;
  border: none;
  font-size: var(--font-size-lg);
  opacity: 0.6;
}

.btn-close:hover {
  opacity: 1;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #004085;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.btn-success:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  opacity: 0.65;
}

/* Survey Dialog Styles */
.modal-lg {
  max-width: 800px;
}

.survey-subtitle {
  font-size: var(--font-size-sm);
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.form-label {
  margin-bottom: 0.75rem;
  color: #495057;
}

.form-check {
  margin-bottom: 0.5rem;
}

.form-check-input {
  margin-top: 0.25rem;
}

.form-check-label {
  margin-left: 0.5rem;
  color: #6c757d;
}

.text-danger {
  color: #dc3545 !important;
}

textarea.form-control {
  resize: vertical;
  min-height: 120px;
  max-height: none;
  overflow-y: auto;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
}

textarea.form-control:focus {
  border-color: #009CAD;
  box-shadow: 0 0 0 0.2rem rgba(0, 156, 173, 0.25);
}

textarea.textarea-multiple-line {
  min-height: 150px;
}

.spinner-border {
  color: #009CAD;
}

.display-4 {
  font-size: var(--font-size-4xl);
  opacity: 0.3;
}

.form-control[readonly] {
  background-color: #f8f9fa;
  opacity: 1;
}

.form-check-input[disabled] {
  opacity: 0.7;
}
</style>
